
import React, { useState } from "react";
import { RibaStage, Project, Activity } from "@/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProject } from "@/contexts/ProjectContext";
import { toast } from "@/hooks/use-toast";
import { Calendar as CalendarIcon, Check, Clock, List, Plus, User } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

interface StageTasksProps {
  project: Project;
  stage: RibaStage;
  onUpdate?: () => void;
}

export default function StageTasks({ project, stage, onUpdate }: StageTasksProps) {
  const { updateActivity } = useProject();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newActivity, setNewActivity] = useState<Partial<Activity>>({
    name: "",
    description: "",
    status: "not-started",
    assignedTo: "",
    dueDate: new Date(),
    stage: stage.id,
    isRequired: true
  });
  
  const handleAddActivity = () => {
    // In a real implementation, would add to the project context
    toast({
      title: "Activity added",
      description: `${newActivity.name} has been added to stage ${stage.id}`
    });
    
    // Reset the form
    setNewActivity({
      name: "",
      description: "",
      status: "not-started",
      assignedTo: "",
      dueDate: new Date(),
      stage: stage.id,
      isRequired: true
    });
    
    setIsDialogOpen(false);
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <Check className="h-4 w-4 text-green-500" />;
      case "in-progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Tasks</CardTitle>
          <CardDescription>Tasks and activities for this stage</CardDescription>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Task</DialogTitle>
              <DialogDescription>
                Create a new task for this stage.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Task Name</label>
                <Input 
                  placeholder="Enter task name"
                  value={newActivity.name}
                  onChange={(e) => setNewActivity({...newActivity, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea 
                  placeholder="Describe the task"
                  value={newActivity.description}
                  onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Assign To</label>
                <Input 
                  placeholder="Enter team member's name"
                  value={newActivity.assignedTo}
                  onChange={(e) => setNewActivity({...newActivity, assignedTo: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Due Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newActivity.dueDate ? (
                        format(newActivity.dueDate, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newActivity.dueDate}
                      onSelect={(date) => setNewActivity({...newActivity, dueDate: date || new Date()})}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select 
                  onValueChange={(value) => 
                    setNewActivity({...newActivity, status: value as "not-started" | "in-progress" | "completed"})}
                  defaultValue="not-started"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-started">Not Started</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  id="required" 
                  checked={newActivity.isRequired}
                  onChange={(e) => setNewActivity({...newActivity, isRequired: e.target.checked})}
                />
                <label htmlFor="required" className="text-sm">Required for stage completion</label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddActivity}>Add Task</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {stage.activities && stage.activities.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Due Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stage.activities.map((activity) => (
                <TableRow key={activity.id} className="cursor-pointer hover:bg-muted">
                  <TableCell className="font-medium">{activity.name}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      activity.status === "completed" 
                        ? "bg-green-100 text-green-800" 
                        : activity.status === "in-progress"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}>
                      {getStatusIcon(activity.status)}
                      <span className="ml-1">
                        {activity.status === "completed" 
                          ? "Completed" 
                          : activity.status === "in-progress" 
                          ? "In Progress" 
                          : "Not Started"}
                      </span>
                    </span>
                  </TableCell>
                  <TableCell>{activity.assignedTo || "Unassigned"}</TableCell>
                  <TableCell>{activity.dueDate.toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8">
            <List className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No tasks for this stage</p>
            <Button className="mt-4" variant="outline" onClick={() => setIsDialogOpen(true)}>
              Add Task
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
